import React from 'react';
import { motion, AnimatePresence } from "motion/react";
import type { Perk } from '../types/eventBookingCategoryTypes';
import { PerkIcon } from './PerkIcon';

// Icons
const SparklesIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className="w-4 h-4 mr-2"
  >
    <path d="M9.42 6.23a2.5 2.5 0 0 0-3.65 3.65l-1.35 1.35a.5.5 0 0 0 0 .7l.7.7a.5.5 0 0 0 .7 0l1.35-1.35a2.5 2.5 0 0 0 3.65-3.65Z"></path>
    <path d="M14.58 11.35a2.5 2.5 0 0 0 3.65-3.65l1.35-1.35a.5.5 0 0 0 0-.7l-.7-.7a.5.5 0 0 0-.7 0l-1.35 1.35a2.5 2.5 0 0 0-3.65 3.65Z"></path>
    <path d="M2 12h3"></path>
    <path d="M19 12h3"></path>
    <path d="M12 2v3"></path>
    <path d="M12 19v3"></path>
  </svg>
);

interface PerksPopoverProps {
  perks: Perk[];
  isVisible: boolean;
}

/**
 * Popover component for displaying booking category perks
 */
export const PerksPopover: React.FC<PerksPopoverProps> = ({ perks, isVisible }) => {
  if (!perks || perks.length === 0) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.95 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="absolute bottom-full left-0 mb-3 w-72 bg-[#0a0a0a]/95 backdrop-blur-xl border border-white/5 rounded-2xl p-4 shadow-2xl z-20"
        >
          <div className="flex items-center mb-4 text-white font-black uppercase tracking-widest text-[11px] opacity-60">
            <SparklesIcon />
            Included Perks
          </div>
          <div className="flex flex-col gap-2">
            {perks.map((perk, index) => (
              <div 
                key={index} 
                className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/5 border border-white/5 group transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-[#111] flex items-center justify-center border border-white/5 shadow-inner">
                  <PerkIcon 
                    icon={perk.icon} 
                    className="w-4 h-4 text-gray-300 group-hover:text-blue-400 transition-colors" 
                  />
                </div>
                <span className="text-[13px] font-bold text-gray-200 uppercase tracking-wide">
                  {perk.name}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
